import React from 'react';
import Icon from '../../../components/AppIcon';
import ProgressiveImage from '../../../components/ui/ProgressiveImage';
import { useLanguage } from '../../../contexts/LanguageContext';

const PropertyCard = ({ listing, onClick, isSelected, onToggleSelect, compareMode }) => {
  const property = listing?.properties;
  const mainImage = listing?.media?.[0]?.enhanced_url || listing?.media?.[0]?.original_url || '/assets/images/no_image.png';
  const { language, t } = useLanguage();
  
  const isUAE = listing?.region === 'uae';
  const isBangladesh = listing?.region === 'bangladesh';

  const formatPrice = (price) => {
    if (!price) return t('contactForPrice') || 'Contact for price';
    if (isUAE) {
      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 0
      })?.format(price);
    }
    if (isBangladesh) {
      // Format in BDT with ৳ symbol
      if (price >= 10000000) {
        return `৳${(price / 10000000)?.toFixed(2)} কোটি`;
      } else if (price >= 100000) {
        return `৳${(price / 100000)?.toFixed(1)} লাখ`;
      }
      return `৳${new Intl.NumberFormat('en-BD')?.format(price)}`;
    }
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    })?.format(price);
  };

  const getListingTypeLabel = (type) => {
    if (language === 'bn') {
      const labels = {
        'buy': 'বিক্রয়ের জন্য',
        'rent': 'ভাড়ার জন্য',
        'commercial': 'বাণিজ্যিক',
        'off-market': 'অফ-মার্কেট',
      };
      return labels?.[type] || type;
    }
    const labels = {
      'buy': 'For Sale',
      'rent': 'For Rent',
      'commercial': 'Commercial',
      'off-market': 'Off-Market',
    };
    return labels?.[type] || type;
  };

  const handleCheckboxClick = (e) => {
    e?.stopPropagation();
    onToggleSelect?.(listing);
  };

  return (
    <div
      onClick={onClick}
      className="bg-card rounded-xl border overflow-hidden hover:shadow-elevation-lg transition-all duration-200 cursor-pointer group relative"
      style={{
        borderColor: isSelected ? 'rgba(0, 212, 255, 0.6)' : 'var(--color-border)',
        boxShadow: isSelected ? '0 0 0 2px rgba(0, 212, 255, 0.2)' : undefined
      }}
    >
      {/* Selection checkbox */}
      <button
        onClick={handleCheckboxClick}
        className="absolute top-3 left-3 z-10 w-7 h-7 rounded-lg flex items-center justify-center transition-all"
        style={{
          background: isSelected ? 'rgba(0, 212, 255, 0.9)' : 'rgba(10, 22, 40, 0.75)',
          border: isSelected ? '2px solid #00D4FF' : '2px solid rgba(0, 212, 255, 0.4)',
          backdropFilter: 'blur(4px)'
        }}
        title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
      >
        {isSelected ? (
          <Icon name="Check" size={14} color="white" />
        ) : (
          <Icon name="Plus" size={14} color="#00D4FF" />
        )}
      </button>

      {/* Progressive image with blur-up effect */}
      <div className="relative h-48 overflow-hidden">
        <ProgressiveImage
          src={mainImage}
          alt={`Property at ${property?.address}`}
          className="w-full h-full group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
          {getListingTypeLabel(listing?.listing_type)}
        </div>
      </div>

      <div className="p-4">
        <div className="mb-3">
          <h3 className="text-xl font-bold text-foreground mb-1">
            {formatPrice(listing?.price_or_rent)}
            {listing?.listing_type === 'rent' && (
              <span className="text-sm font-normal text-muted-foreground ml-1">
                {isUAE ? (language === 'bn' ? '/ বছর' : '/ year') : (language === 'bn' ? '/ সপ্তাহ' : '/ week')}
              </span>
            )}
          </h3>
          <p className="text-sm text-muted-foreground">
            {property?.address}, {property?.suburb} {property?.state} {property?.postcode}
          </p>
        </div>

        <div className="flex items-center gap-4 mb-3 text-sm text-muted-foreground">
          {property?.beds && (
            <div className="flex items-center gap-1">
              <Icon name="Bed" size={16} color="currentColor" />
              <span>{property?.beds}</span>
            </div>
          )}
          {property?.baths && (
            <div className="flex items-center gap-1">
              <Icon name="Bath" size={16} color="currentColor" />
              <span>{property?.baths}</span>
            </div>
          )}
          {property?.parking && (
            <div className="flex items-center gap-1">
              <Icon name="Car" size={16} color="currentColor" />
              <span>{property?.parking}</span>
            </div>
          )}
          <div className="flex items-center gap-1 capitalize">
            <Icon name="Home" size={16} color="currentColor" />
            <span>{property?.type}</span>
          </div>
        </div>

        {listing?.description_short && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {listing?.description_short}
          </p>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;